// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Breach Contributors

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Image,
  Film,
  ScanText,
  Zap,
  Settings,
  Menu,
  X,
} from 'lucide-react';
import { useStore } from '@/store';

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  pro?: boolean;
  children: { label: string; path: string }[];
}

const GROUPS: NavGroup[] = [
  {
    label: 'PDF',
    icon: <FileText className="h-5 w-5" />,
    children: [
      { label: 'Compress', path: '/pdf/compress' },
      { label: 'Merge', path: '/pdf/merge' },
      { label: 'Split', path: '/pdf/split' },
      { label: 'Convert', path: '/pdf/convert' },
    ],
  },
  {
    label: 'Images',
    icon: <Image className="h-5 w-5" />,
    children: [
      { label: 'Compress', path: '/images/compress' },
      { label: 'Convert', path: '/images/convert' },
      { label: 'Resize/Optimize', path: '/images/resize' },
    ],
  },
  {
    label: 'Media (A/V)',
    icon: <Film className="h-5 w-5" />,
    children: [
      { label: 'Compress', path: '/media/compress' },
      { label: 'Convert', path: '/media/convert' },
      { label: 'Trim/Extract', path: '/media/trim' },
    ],
  },
  {
    label: 'OCR & Text',
    icon: <ScanText className="h-5 w-5" />,
    pro: true,
    children: [
      { label: 'Scan → Searchable PDF', path: '/ocr/scan' },
      { label: 'Extract Text', path: '/ocr/extract' },
    ],
  },
  {
    label: 'Workflows',
    icon: <Zap className="h-5 w-5" />,
    pro: true,
    children: [
      { label: 'Batch Process', path: '/workflows/batch' },
      { label: 'Custom Presets', path: '/workflows/presets' },
      { label: 'Offline PWA', path: '/workflows/offline' },
    ],
  },
];

function ProBadge() {
  return (
    <span className="ml-auto rounded bg-breach-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-breach-100">
      Pro
    </span>
  );
}

export default function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useStore();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const renderGroup = (group: NavGroup) => {
    const open = expanded[group.label] ?? true;
    const anyActive = group.children.some((c) => isActive(c.path));

    return (
      <div key={group.label} className="mb-1">
        <button
          onClick={() =>
            setExpanded((prev) => ({ ...prev, [group.label]: !prev[group.label] }))
          }
          className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-breach-700 ${
            anyActive ? 'bg-breach-700 text-white' : 'text-neutral-400'
          }`}
        >
          {group.icon}
          <span className={`flex-1 text-left ${!sidebarOpen && 'md:hidden'}`}>{group.label}</span>
          {group.pro && sidebarOpen && <ProBadge />}
          {sidebarOpen && (
            <ChevronRight
              className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
            />
          )}
        </button>
        {open && sidebarOpen && (
          <div className="ml-4 mt-1 space-y-1 border-l border-neutral-700 pl-3">
            {group.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                onClick={() => setMobileOpen(false)}
                className={`block rounded-md px-3 py-1.5 text-sm transition-colors hover:bg-breach-700 ${
                  isActive(child.path) ? 'text-white' : 'text-neutral-400'
                }`}
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        className="fixed left-4 top-4 z-50 rounded-md bg-breach-800 p-2 text-breach-100 shadow-lg md:hidden"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="h-6 w-6" />
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 flex h-full flex-col border-r border-neutral-800 bg-breach-800 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <Link
            to="/"
            className={`flex items-center gap-2 text-lg font-bold ${!sidebarOpen && 'md:hidden'}`}
            onClick={() => setMobileOpen(false)}
          >
            <span className="text-xl">🐎</span>
            <span>Breach</span>
          </Link>
          <button
            onClick={toggleSidebar}
            className="hidden rounded p-1 text-neutral-400 hover:bg-breach-700 hover:text-white md:block"
          >
            {sidebarOpen ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded p-1 text-neutral-400 hover:bg-breach-700 hover:text-white md:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mx-3 my-2 h-px bg-neutral-700" />

        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-breach-700 ${
              location.pathname === '/' ? 'bg-breach-700 text-white' : 'text-neutral-400'
            }`}
          >
            <span className="text-lg">🐎</span>
            <span className={`${!sidebarOpen && 'md:hidden'}`}>Home</span>
          </Link>
          {GROUPS.map(renderGroup)}
        </nav>

        <div className="mx-3 my-2 h-px bg-neutral-700" />

        <div className="px-2 pb-4">
          <Link
            to="/settings"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-breach-700 ${
              location.pathname.startsWith('/settings')
                ? 'bg-breach-700 text-white'
                : 'text-neutral-400'
            }`}
          >
            <Settings className="h-5 w-5" />
            <span className={`${!sidebarOpen && 'md:hidden'}`}>Settings</span>
          </Link>
        </div>
      </aside>
    </>
  );
}
